import type {
  TrackInitOptions,
  PvOptions,
  TraceData,
  TrackerAdapter,
  TrackPublicAPI,
} from './types';
import {
  PERF_EVENT_PREFIX,
  MANUAL_STAY_EVENT_NAME,
  DEFAULT_ERROR_SAMPLE_RATE,
  DEFAULT_ERROR_MAX_COUNT,
  DEFAULT_ERROR_DEDUP_WINDOW,
  DEFAULT_PAGE_STAY_MIN_DURATION,
} from './constants';
import { isBrowser, getPageUrl } from './utils';
import { loadSDK } from './loader';
import { DataRangersAdapter } from './adapters/datarangers';
import { BatchedAdapter } from './batcher';
import { DedupAdapter } from './deduper';
import { SamplingAdapter } from './sampler';
import { DEFAULT_BATCH_INTERVAL, DEFAULT_EVENT_DEDUP_INTERVAL } from './constants';
import { setupPerfObservers } from './modules/perf';
import { setupErrorObservers, type ErrorReporter } from './modules/error';
import { setupPageStayObserver, StayTimer } from './modules/stay';

/** Default init options */
const DEFAULT_OPTIONS: Partial<TrackInitOptions> = {
  enableDebug: false,
  disableSdkMonitor: true,
  reportFcp: true,
  reportLcp: true,
  reportError: true,
  reportResourceError: true,
  errorSampleRate: DEFAULT_ERROR_SAMPLE_RATE,
  errorMaxCount: DEFAULT_ERROR_MAX_COUNT,
  errorDedupWindow: DEFAULT_ERROR_DEDUP_WINDOW,
  errorIgnoreList: [],
  reportPageStay: false,
  pageStayMinDuration: DEFAULT_PAGE_STAY_MIN_DURATION,
};

/** Print a prominent styled console warning */
function warnStyled(message: string): void {
  console.warn(
    '%c[yl-nsm-track] ' + message,
    'background: #FF4444; color: #FFFFFF; font-size: 14px; font-weight: bold; padding: 6px 10px; border-radius: 4px;',
  );
}

/**
 * TrackSDK — main orchestrator class.
 * Wraps the underlying analytics SDK and exposes a clean API.
 */
class TrackSDK {
  private adapter: TrackerAdapter;
  private _ready = false;
  private _initPromise?: Promise<void>;
  private _pendingCalls: Array<() => void> = [];
  private _errorReporter?: ErrorReporter;
  private _stayTimer = new StayTimer();
  private _pageStayObserver?: ReturnType<typeof setupPageStayObserver>;

  constructor() {
    this.adapter = new DataRangersAdapter();
  }

  /**
   * Initialize the tracking SDK.
   * Mounts the public API immediately, then loads the SDK script,
   * calls init + start, sets up perf observers, and flushes queued calls.
   */
  init(appId: number | string, options?: Omit<TrackInitOptions, 'appId'>): Promise<void> {
    if (this._ready) {
      console.warn('[yl-nsm-track] Already initialized. Skipping re-init.');
      return Promise.resolve();
    }

    if (this._initPromise) {
      console.warn('[yl-nsm-track] Initialization already in progress. Reusing init promise.');
      return this._initPromise;
    }

    // 兼容处理：如果 appId 是 string 类型，自动转为 number
    const normalizedAppId = typeof appId === 'string' ? Number(appId) : appId;

    // Mount first so SPA route hooks can safely call window.$track immediately after init().
    this._mountToWindow();

    this._initPromise = this._initialize(normalizedAppId, options).catch((error) => {
      this._initPromise = undefined;
      throw error;
    });

    return this._initPromise;
  }

  /** Track a custom event (single event, NOT batch) */
  event(eventCode: string, properties?: Record<string, any>): void {
    if (!eventCode || typeof eventCode !== 'string') {
      warnStyled('$event() requires a non-empty eventCode string. Call skipped.');
      return;
    }
    if (!properties || !properties.event_name || typeof properties.event_name !== 'string') {
      warnStyled('$event() requires event_name in properties. Call skipped.');
      return;
    }
    const payload = { ...properties };
    this._runWhenReady(() => this.adapter.event(eventCode, payload));
  }

  /** Report a page view */
  pv(options?: PvOptions): void {
    // SPA 路由切换：上报当前页面停留时长并重置计时
    this._pageStayObserver?.reportCurrent();

    // SPA 路由切换：重置错误限频计数与去重缓存，让新页面获得全新的错误预算
    this._errorReporter?.resetCount();

    const payload = options
      ? {
          ...options,
          extra: options.extra ? { ...options.extra } : options.extra,
        }
      : undefined;
    this._runWhenReady(() => this.adapter.pageView(payload));
  }

  /** Report a performance metric manually */
  trace(metricName: string, data: TraceData): void {
    const eventName = `${PERF_EVENT_PREFIX}${metricName.toLowerCase()}`;
    const payload = {
      metric_name: metricName,
      value: data.value,
      rating: data.rating,
      page_url: getPageUrl(),
      ...Object.fromEntries(
        Object.entries(data).filter(([k]) => !['value', 'rating'].includes(k)),
      ),
    };

    this._runWhenReady(() => this.adapter.event(eventName, payload));
  }

  /** 手动上报错误（不经过采样，但仍受去重 / 限频约束） */
  captureError(error: Error | string, extra?: Record<string, any>): void {
    this._runWhenReady(() => {
      if (this._errorReporter) {
        this._errorReporter.captureError(error, extra);
      }
    });
  }

  /** 手动上报停留时长（一次性，已知时长） */
  stay(name: string, durationMs: number, extra?: Record<string, any>): void {
    const payload: Record<string, any> = {
      stay_name: name,
      duration_ms: durationMs,
      duration_seconds: Math.round(durationMs / 1000),
      ...(extra || {}), // 自定义属性平铺到最外层，可覆盖默认值
    };
    this._runWhenReady(() => this.adapter.event(MANUAL_STAY_EVENT_NAME, payload));
  }

  /** 开始计时（与 stayEnd 配对使用） */
  stayStart(name: string): void {
    this._stayTimer.start(name);
  }

  /** 结束计时并上报 */
  stayEnd(name: string, extra?: Record<string, any>): void {
    const result = this._stayTimer.end(name);
    if (!result) return; // 没有找到对应计时器，已打印警告

    this.stay(name, result.durationMs, extra);
  }

  /** Set user identity for subsequent events; does not emit a login event. */
  login(userId: string): void {
    this._runWhenReady(() => this.adapter.login(userId));
  }

  /** Clear user identity; does not emit a logout event. */
  logout(): void {
    this._runWhenReady(() => this.adapter.logout());
  }

  private async _initialize(
    appId: number,
    options?: Omit<TrackInitOptions, 'appId'>,
  ): Promise<void> {
    const opts: TrackInitOptions = {
      ...DEFAULT_OPTIONS,
      ...options,
      appId,
    } as TrackInitOptions;

    // Step 1: Validate required fields
    if (!opts.channelDomain) {
      warnStyled('init() requires channelDomain to be set. Initialization skipped.');
      return;
    }
    if (!opts.sdkScriptUrl) {
      warnStyled('init() requires sdkScriptUrl to be set. Initialization skipped.');
      return;
    }

    // Step 2: Load the SDK script (creates command queue stub first)
    await loadSDK(opts.sdkScriptUrl);

    // Step 3: 如果配置了事件采样率，用 SamplingAdapter 包装
    if (opts.eventSampling && Object.keys(opts.eventSampling).length > 0) {
      this.adapter = new SamplingAdapter(this.adapter, {
        eventSampling: opts.eventSampling,
      });
    }

    // Step 4: 如果启用了事件去重，用 DedupAdapter 包装底层 adapter
    const dedupInterval = opts.eventDedupInterval ?? DEFAULT_EVENT_DEDUP_INTERVAL;
    if (dedupInterval > 0) {
      this.adapter = new DedupAdapter(this.adapter, { dedupInterval });
    }

    // Step 5: 如果启用了批量上报，用 BatchedAdapter 包装底层 adapter
    if (opts.enableBatch !== false) {
      this.adapter = new BatchedAdapter(this.adapter, {
        batchInterval: opts.batchInterval ?? DEFAULT_BATCH_INTERVAL,
      });
    }

    // Step 6: Init and start the SDK
    const initConfig: Record<string, any> = {};
    if (opts.channelDomain) initConfig.channel_domain = opts.channelDomain;
    if (opts.enableDebug) initConfig.enable_debug = true;
    if (opts.disableSdkMonitor) initConfig.disable_sdk_monitor = true;
    if (opts.globalProperties) initConfig.auto_track_properties = opts.globalProperties;

    this.adapter.init(appId, initConfig);
    this.adapter.start();

    // Step 6: Mark ready and replay calls made while the SDK was loading
    this._ready = true;
    this._flushPendingCalls();

    // Step 7: Setup FCP/LCP performance observers
    setupPerfObservers(this.adapter, {
      reportFcp: opts.reportFcp !== false,
      reportLcp: opts.reportLcp !== false,
      debug: opts.enableDebug === true,
    });

    // Step 8: 注册错误观察器（运行时 / Promise / 资源）
    this._errorReporter = setupErrorObservers(this.adapter, {
      reportError: opts.reportError !== false,
      reportResourceError: opts.reportResourceError !== false,
      sampleRate: opts.errorSampleRate ?? DEFAULT_ERROR_SAMPLE_RATE,
      maxCount: opts.errorMaxCount ?? DEFAULT_ERROR_MAX_COUNT,
      dedupWindow: opts.errorDedupWindow ?? DEFAULT_ERROR_DEDUP_WINDOW,
      ignoreList: opts.errorIgnoreList ?? [],
      debug: opts.enableDebug === true,
    });

    // Step 9: 初始化页面停留时长自动采集
    if (opts.reportPageStay !== false) {
      this._pageStayObserver = setupPageStayObserver(this.adapter, {
        minDuration: opts.pageStayMinDuration,
        debug: opts.enableDebug === true,
      });
    }
  }

  private _runWhenReady(fn: () => void): void {
    if (this._ready) {
      fn();
      return;
    }

    if (!this._initPromise) {
      fn();
      return;
    }

    this._pendingCalls.push(fn);
  }

  private _flushPendingCalls(): void {
    const calls = this._pendingCalls.splice(0);

    for (const call of calls) {
      try {
        call();
      } catch (error) {
        console.warn('[yl-nsm-track] Failed to flush queued call', error);
      }
    }
  }

  /** Mount public API onto window.$track */
  private _mountToWindow(): void {
    if (!isBrowser()) return;

    const api: TrackPublicAPI = {
      $event: this.event.bind(this),
      $pv: this.pv.bind(this),
      $trace: this.trace.bind(this),
      $error: this.captureError.bind(this),
      $stay: this.stay.bind(this),
      $stayStart: this.stayStart.bind(this),
      $stayEnd: this.stayEnd.bind(this),
      $login: this.login.bind(this),
      $logout: this.logout.bind(this),
    };

    try {
      Object.defineProperty(window, '$track', {
        value: api,
        writable: false,
        configurable: true, // allows re-init or cleanup
      });
    } catch {
      // Fallback for environments where defineProperty fails
      (window as any).$track = api;
    }
  }
}

/** Singleton instance */
export const trackSDK = new TrackSDK();
